<template>
  <v-card class="mb-4 filter-performance-card">
    <v-card-text class="pa-8">
      <!-- Filter Section -->
      <div class="filter-section mb-4">
        <div class="filter-label mb-2">Filter by:</div>
        <div class="d-flex flex-wrap filter-buttons align-center">
          <v-btn
            v-for="filter in filterOptions"
            :key="filter.value"
            :class="[
              'filter-btn',
              { 'filter-btn-active': selectedFilter === filter.value },
            ]"
            @click="applyFilter(filter.value)"
            :outlined="selectedFilter !== filter.value"
            :color="selectedFilter === filter.value ? 'primary' : 'grey'"
            small
            class="mx-2 my-2"
          >
            {{ filter.label }}
          </v-btn>
          <!-- Custom Date Range Button -->
          <v-btn
            :class="[
              'filter-btn',
              {
                'filter-btn-active':
                  selectedFilter === 'custom' || showCustomDatePicker,
              },
            ]"
            @click="handleCustomButtonClick"
            :outlined="selectedFilter !== 'custom' && !showCustomDatePicker"
            :color="
              selectedFilter === 'custom' || showCustomDatePicker
                ? 'primary'
                : 'grey'
            "
            small
            class="mx-2 my-2"
          >
            Custom
          </v-btn>
          <!-- Date Range Picker - Show only when Custom button is clicked -->
          <div v-if="showCustomDatePicker" class="custom-date-picker-wrapper">
            <f-date-select-range-field
              :value="customDateRange"
              label="Select Date Range"
              :minimumDate="minDateMoment"
              :maximumDate="maxDateMoment"
              dateDisplayFormat="DD MMM YYYY"
              :onSelect="handleCustomDateRangeSelect"
              dense
              outlined
              class="custom-date-input"
            ></f-date-select-range-field>
          </div>
        </div>
      </div>

      <!-- Performance Overview Section -->
      <div class="performance-section">
        <div class="section-header mb-3">
          <h2 class="section-title">Performance Overview</h2>
          <p class="section-subtitle">Key metrics for the selected period</p>
        </div>

        <v-row class="summary-cards-row" no-gutters>
          <!-- Total Links Sent -->
          <v-col cols="12" sm="6" md="4" lg="2" class="summary-card-col px-1">
            <v-card class="summary-card summary-card-blue" flat>
              <v-card-text class="pa-3 d-flex flex-column justify-center">
                <div class="d-flex align-center justify-center mb-2">
                  <v-icon color="#4A90E2" size="20" class="mr-1"
                    >mdi-link-variant</v-icon
                  >
                  <div class="summary-label">Lead Added</div>
                </div>
                <div class="summary-value text-center">
                  {{ dsaAgentPerformanceData.linkSentCount || 0 }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Total Signups -->
          <v-col cols="12" sm="6" md="4" lg="2" class="summary-card-col px-1">
            <v-card class="summary-card summary-card-blue" flat>
              <v-card-text class="pa-3 d-flex flex-column justify-center">
                <div class="d-flex align-center justify-center mb-2">
                  <v-icon color="#4A90E2" size="20" class="mr-1"
                    >mdi-account-plus</v-icon
                  >
                  <div class="summary-label">Total Signups</div>
                </div>
                <div class="summary-value text-center">
                  {{ dsaAgentPerformanceData.signUpCount || 0 }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Verified Credit Pulls -->
          <v-col cols="12" sm="6" md="4" lg="2" class="summary-card-col px-1">
            <v-card class="summary-card summary-card-yellow" flat>
              <v-card-text class="pa-3 d-flex flex-column justify-center">
                <div class="d-flex align-center justify-center mb-2">
                  <v-icon color="#F5A623" size="20" class="mr-1"
                    >mdi-credit-card</v-icon
                  >
                  <div class="summary-label">Credit Pulls</div>
                </div>
                <div class="summary-value text-center">
                  {{ dsaAgentPerformanceData.creditPullCount || 0 }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- DRP Eligible Clients -->
          <v-col cols="12" sm="6" md="4" lg="2" class="summary-card-col px-1">
            <v-card class="summary-card summary-card-green" flat>
              <v-card-text class="pa-3 d-flex flex-column justify-center">
                <div class="d-flex align-center justify-center mb-2">
                  <v-icon color="#7ED321" size="20" class="mr-1"
                    >mdi-check-circle</v-icon
                  >
                  <div class="summary-label">DRP Eligible</div>
                </div>
                <div class="summary-value text-center">
                  {{ dsaAgentPerformanceData.elgibileDRPCount || 0 }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- DCP Eligible Clients -->
          <v-col cols="12" sm="6" md="4" lg="2" class="summary-card-col px-1">
            <v-card class="summary-card summary-card-green" flat>
              <v-card-text class="pa-3 d-flex flex-column justify-center">
                <div class="d-flex align-center justify-center mb-2">
                  <v-icon color="#7ED321" size="20" class="mr-1"
                    >mdi-check-circle</v-icon
                  >
                  <div class="summary-label">DCP Eligible</div>
                </div>
                <div class="summary-value text-center">
                  {{ dsaAgentPerformanceData.elgibileDCPCount || 0 }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Ineligible Clients -->
          <v-col cols="12" sm="6" md="4" lg="2" class="summary-card-col px-1">
            <v-card class="summary-card summary-card-red" flat>
              <v-card-text class="pa-3 d-flex flex-column justify-center">
                <div class="d-flex align-center justify-center mb-2">
                  <v-icon color="#D0021B" size="20" class="mr-1"
                    >mdi-close-circle</v-icon
                  >
                  <div class="summary-label">Ineligible</div>
                </div>
                <div class="summary-value text-center">
                  {{ dsaAgentPerformanceData.inEligibleCount || 0 }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Transferred -->
          <v-col cols="12" sm="6" md="4" lg="2" class="summary-card-col px-1">
            <v-card class="summary-card summary-card-green" flat>
              <v-card-text class="pa-3 d-flex flex-column justify-center">
                <div class="d-flex align-center justify-center mb-2">
                  <v-icon color="#7ED321" size="20" class="mr-1"
                    >mdi-swap-horizontal</v-icon
                  >
                  <div class="summary-label">Transferred</div>
                </div>
                <div class="summary-value text-center">
                  {{ dsaAgentPerformanceData.transferredCount || 0 }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import moment from "moment";
import Helper from "@/util/Helper";
import FDateSelectRangeField from "@/components/form/field/FDateSelectRangeField.vue";
import store, * as Store from "@/../src-gen/store";
import * as Data from "@/../src-gen/data";
import * as Action from "@/../src-gen/action";

@Component({
  components: {
    FDateSelectRangeField,
  },
})
export default class FPPSalesDashboardFilterMetrics extends Vue {
  @Prop()
  selectedFilter: string;

  @Prop()
  customDateRange: [string, string];

  @Prop()
  metrics: any;

  @Prop()
  dsaAgentPerformanceData: Data.Partner.DsaAgentPerformanceData;

  filterOptions = [
    { label: "Today", value: "today" },
    { label: "Last 7 Days", value: "last7Days" },
    { label: "Last 30 Days", value: "last30Days" },
  ];

  showCustomDatePicker: boolean = false;

  get minDateMoment() {
    return moment().subtract(2, "year");
  }

  get maxDateMoment() {
    return moment();
  }

  @Watch("selectedFilter")
  onSelectedFilterChange(newValue: string) {
    // Show date picker if custom filter is selected
    if (newValue === "custom") {
      this.showCustomDatePicker = true;
    } else {
      this.showCustomDatePicker = false;
    }
  }

  @Watch("customDateRange")
  onCustomDateRangeChange(newValue: [string, string]) {
    // Show date picker if custom date range has values
    if (newValue && newValue[0] && newValue[1]) {
      this.showCustomDatePicker = true;
    }
  }

  mounted() {
    // Show date picker if custom filter is already selected or if custom date range exists
    if (
      this.selectedFilter === "custom" ||
      (this.customDateRange &&
        this.customDateRange[0] &&
        this.customDateRange[1])
    ) {
      this.showCustomDatePicker = true;
    }
  }

  applyFilter(filterValue: string) {
    // Hide custom date picker when other filters are selected
    if (filterValue !== "custom") {
      this.showCustomDatePicker = false;
    }
    this.$emit("filterChanged", filterValue);
  }

  handleCustomButtonClick() {
    this.showCustomDatePicker = !this.showCustomDatePicker;
    if (this.showCustomDatePicker) {
      this.$emit("filterChanged", "custom");
    } else {
      // If hiding, reset to today filter
      this.$emit("filterChanged", "today");
    }
  }

  handleCustomDateRangeSelect(dates: [string, string]) {
    this.$emit("dateRangeChanged", dates);
  }
}
</script>

<style lang="scss" scoped>
.filter-performance-card {
  background: #ffffff;
  border-radius: 8px;
  // box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--primary-color);
}

.filter-section {
  .filter-label {
    font-size: 18px;
    font-weight: 500;
    color: #333;
    margin-bottom: 8px;
  }

  .filter-btn {
    text-transform: none;
    border-radius: 6px;
    font-weight: 500;
    min-width: 140px;
    height: 36px;
  }

  .custom-date-picker-wrapper {
    margin-left: 8px;
    display: flex;
    align-items: center;
    height: 36px;

    .custom-date-input {
      min-width: 140px;
      height: 36px;
    }
  }
}

.performance-section {
  .section-title {
    font-size: 18px;
    font-weight: 500;
    color: #000;
    margin: 0;
  }

  .section-subtitle {
    font-size: 14px;
    color: #666;
    margin: 4px 0 0 0;
  }

  .summary-cards-row {
    display: flex;
    align-items: stretch;
    flex-wrap: nowrap;
  }

  .summary-card-col {
    display: flex;
    flex: 1;
    min-width: 0;
  }
}

.summary-card {
  border-radius: 8px;
  transition: transform 0.2s;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;

  &:hover {
    transform: translateY(-2px);
    // box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &.summary-card-blue {
    background: #e3f2fd;
  }

  &.summary-card-yellow {
    background: #fff9c4;
  }

  &.summary-card-green {
    background: #e8f5e9;
  }

  &.summary-card-red {
    background: #ffebee;
  }

  .summary-value {
    font-size: 28px;
    font-weight: bold;
    color: #000;
    margin: 4px 0;
    line-height: 1.2;
  }

  .summary-label {
    font-size: 13px;
    color: #666;
    font-weight: 500;
    line-height: 1.4;
  }
}
</style>
